/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import * as React from "react";
//import { LocalizationHandler } from "@emt/emt-localization/lib/localization-handler";
import { LocalizationInfo } from "@emt/emt-localization/lib/localization-info";
import { Provider, Store } from "react-redux";
import { createStore } from "redux";
import { reducers } from "../redux/reducers";
import { userLocale } from "@emt/emt-localization/lib/user-locale";
import { Metadata } from "@emt/emt-metadata-client";
import { EnrollmentMetadataClient } from "../clients/enrollment-metadata-client";
import { EnrollmentAppStore } from "../redux/enrollment-store/store";
import { EnrollmentClient } from "../clients/enrollment-client";
//import { ReportingClient } from "../clients/reporting-client";
import { ActionDispatcher } from "../redux/actions/actions";
import { EnrollmentsPageContainer } from "../containers/enrollments-page-container";
import { BrowserRouter, Route, RouteComponentProps } from "react-router-dom";
import { MainPage } from "@emt/emt-react";
import {
  LocalizationContext,
  defaultLocInfo,
} from "../context/LocalizationContext";
import { Environment } from "../utilities/environment";
import { OfferingPageContainer } from "../containers/offering-page-container";
import { OfferingPageRouteProperties } from "./offering-page";
import { AIContext } from "../context/AIContext";
import { SeverityLevel } from "@microsoft/applicationinsights-web";
import { ai, getAppInsights } from "../telemetry/telemetry-service";
import { History, createBrowserHistory } from "history";
import { Layout, TaskMenu, TaskItem } from "../components/helper";
import { DetailedHTMLProps, HTMLAttributes } from "react";
import { SessionHelper } from "../utilities/sessionHelper"

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'he-shell': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;
      "shell_he-breadcrumb": DetailedHTMLProps<
        HTMLAttributes<HTMLElement>,
        HTMLElement
      >;
    }
  }
}

class AppState {
  localization: LocalizationInfo = defaultLocInfo;
  appInsights: any = {};
  aiInstKey: string = "";
  history: History;
}

export class EnrollmentsApp extends React.Component<{}, AppState> {
  private readonly store: Store<EnrollmentAppStore> =
    createStore<EnrollmentAppStore>(reducers);

  constructor(props: {}) {
    super(props);
    this.state = new AppState();
  }

  updateLocStrings(localization: LocalizationInfo) {
    this.setState({
      localization: localization,
    });
  }

  setupAppInsights() {
    ai.initialize(this.state.aiInstKey, this.state.history);
    this.setState({ appInsights: getAppInsights() });
  }

  componentWillMount() {
    const store = this.store;
    const metadataClient = new EnrollmentMetadataClient();
    const enrollmentClient = new EnrollmentClient();
    //const reportingClient = new ReportingClient();

    var aiInstKey = "4aea2d29-a5ec-424e-be50-18993d11db9e";

    if (Environment.isTst()) {
      aiInstKey = "6cb1a1e5-4ba0-4304-9661-37474799cfa5";
    } else if (Environment.isInt()) {
      aiInstKey = "5a3c9a1f-3501-4993-9b86-fa8718689224";
    }

    this.setState(
      { aiInstKey: aiInstKey, history: createBrowserHistory() },
      this.setupAppInsights
    );

    /*
    let locHandler = new LocalizationHandler(
      () => "/dashboard/incentives/enrollment/api/localization",
      new Map<string, number>([])
    );
    locHandler.get().then((localization) => {
      this.updateLocStrings(localization);
    });
    */

    console.log(`api root is ${process.env.API_ROOT}`);
    enrollmentClient.getLocStrings().then(localization => {
      //console.log("localization : ", localization);
      if (localization) {
        this.updateLocStrings(localization);
      } else {
        console.error("Localization data is null");
      }
    });

    metadataClient
      .getMetaData(userLocale)
      .then((metadata: Metadata) => {
        store.dispatch(ActionDispatcher.storeMetadata(metadata));
        store.dispatch(ActionDispatcher.completedMetadata());
      })
      .catch(() => {
        store.dispatch(ActionDispatcher.completedMetadata());
        store.dispatch(ActionDispatcher.errorMetadata());
        this.state.appInsights.trackException({
          error: new Error("Problem fetching MetaData"),
          severityLevel: SeverityLevel.Error,
        });
      });

    enrollmentClient
      .getInvitations()
      .then((invitations) => {
        invitations = invitations.filter((i) => i.partnerId.type === "MPN");
        store.dispatch(ActionDispatcher.storeInvitations(invitations));
        store.dispatch(ActionDispatcher.completedInvitations());
      })
      .catch(() => {
        store.dispatch(ActionDispatcher.completedInvitations());
        store.dispatch(ActionDispatcher.errorInvitations());
        this.state.appInsights.trackException({
          error: new Error("Problem fetching Invitation data"),
          severityLevel: SeverityLevel.Error,
        });
      });

    /*deprecating call to reportingClient
             if (Environment.isModern()) {
                 reportingClient
                     .getPayoutReports(metadataClient.getMetaData(userLocale))
                     .then(payoutReports => {
                         payoutReports = payoutReports.filter(i => i.partnerId.type === "MPN");
                         store.dispatch(ActionDispatcher.storePayoutReports(payoutReports));
                         store.dispatch(ActionDispatcher.completedPayoutReports());
                     })
                     .catch(() => {
                         store.dispatch(ActionDispatcher.completedPayoutReports());
                         store.dispatch(ActionDispatcher.errorPayoutReports());
                         this.state.appInsights.trackException({
                             error: new Error("Problem fetching Reporting data"),
                             severityLevel: SeverityLevel.Error
                         });
                     });
             } */

    enrollmentClient
      .getEnrollments()
      .then((enrollments) => {
        enrollments = enrollments.filter((i) => i.partnerId.type === "MPN");
        store.dispatch(ActionDispatcher.storeEnrollments(enrollments || []));
        store.dispatch(ActionDispatcher.completedEnrollments());
      })
      .catch(() => {
        store.dispatch(ActionDispatcher.completedEnrollments());
        store.dispatch(ActionDispatcher.errorEnrollments());
      });

    enrollmentClient
      .getBankAndTaxStatus()
      .then((bankAndTaxDetails) => {
        bankAndTaxDetails = bankAndTaxDetails.filter(
          (i) => i.partnerId.type === "MPN"
        );
        store.dispatch(
          ActionDispatcher.storeBankAndTaxStatus(bankAndTaxDetails || [])
        );
        store.dispatch(ActionDispatcher.completedBankAndTaxStatus());
      })
      .catch(() => {
        store.dispatch(ActionDispatcher.completedBankAndTaxStatus());
        store.dispatch(ActionDispatcher.errorBankAndTaxStatus());
      });
  }

  render(): JSX.Element {
    return (
      <Layout>
        <he-shell scope-query-selector="he-shell" slot="header">
          <shell_he-breadcrumb id="extra-crumbs" />
        </he-shell>
        <TaskMenu itemType="page" autoCollapse slot="menu">
          <TaskItem selected>{this.state.localization.strings["LeftNavOverView"]}</TaskItem>
          <TaskItem
            onClick={() => {
              SessionHelper.redirectToV1("claims/plans/overview");
            }}
          >
            {this.state.localization.strings["LeftNavPlans"]}
          </TaskItem>
          <TaskItem
            onClick={() => {
              SessionHelper.redirectToV1("claims/coop/pc");
            }}
          >
            {this.state.localization.strings["LeftNavClaims"]}
          </TaskItem>
          {/* <TaskItem href="/dashboard/incentives/claims/coop/pc" onHeSelectedChange={() => {
                  window.location.href = '/dashboard/incentives/claims/coop/pc';
                }}>Plans management
              </TaskItem> */}
          <TaskItem>
            Microsoft Commerce Incentive
            <TaskItem
              onClick={() => {
                SessionHelper.redirectToV2("customer-association/engagements");
              }}
            >
              {this.state.localization.strings["LeftNavEngagements"]}
            </TaskItem>
            <TaskItem
              onClick={() => {
                SessionHelper.redirectToV2("customer-association/claims");
              }}
            >
              {this.state.localization.strings["LeftNavCustomerClaims"]}
            </TaskItem>
          </TaskItem>

        </TaskMenu>
        <Provider store={this.store}>
          <LocalizationContext.Provider value={this.state.localization}>
            <BrowserRouter>
              <AIContext.Provider value={this.state.appInsights}>
                <div className="row">
                  <MainPage hasFocusNav={Environment.isWorkspace()}>
                    <Route
                      exact
                      path={`/dashboard/v2/incentives/enrollment/(web|summary|modern/web)`}
                      render={() => <EnrollmentsPageContainer />}
                    />
                    <Route
                      exact
                      path={`/dashboard/v2/incentives/enrollment/(web|summary|modern/web)/offerings/:offeringId`}
                      render={(
                        props: RouteComponentProps<OfferingPageRouteProperties>
                      ) => {
                        return <OfferingPageContainer {...props.match.params} />;
                      }}
                    />
                  </MainPage>
                </div>
              </AIContext.Provider>
            </BrowserRouter>
          </LocalizationContext.Provider>
        </Provider>
      </Layout>
    );
  }
}
